import React from 'react';
import {
  WorkspaceFilterBarSkeleton,
  WorkspaceTableSkeleton,
  WorkspaceTabsSkeleton,
  type WorkspaceTableSkeletonColumn,
} from '@/components/reusable/WorkspaceSkeleton';

/**
 * The single loading state for the Forms workspace (`/home/flows`).
 *
 * Opening Forms used to flash four loaders in sequence before the table
 * appeared:
 *   1. a full-page "Loading..." — the `FeatureRoute` / `RoleRoute` gates
 *   2. a bare spinner — the route-level `<Suspense>` fallback in `App.tsx`
 *   3. a centred spinner — `WhatsAppFlows` resolving business accounts
 *   4. four stacked grey bars — the forms query, a shape the table never had
 *
 * All four now render this skeleton, so the layout appears once and stays put
 * until the real workspace replaces it. The columns mirror `FlowTable`,
 * including the two that only appear at `sm` and `lg`.
 */

const FORMS_TABLE_COLUMNS: WorkspaceTableSkeletonColumn[] = [
  { width: 'w-[55%]' },
  { width: 'w-16' },
  { width: 'w-24', cellClassName: 'hidden sm:table-cell' },
  { width: 'w-20', cellClassName: 'hidden lg:table-cell' },
  { width: 'w-8', actions: true },
];

/** The forms table on its own — used while the list loads inside the page. */
export const FormsTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 8 }) => (
  <WorkspaceTableSkeleton
    columns={FORMS_TABLE_COLUMNS}
    rows={rows}
    data-testid="forms-table-skeleton"
  />
);

/** Tabs strip: WhatsApp Forms, Form Submissions. */
export const FormsTabsSkeleton: React.FC = () => <WorkspaceTabsSkeleton widths={[110, 118]} />;

/** Toolbar: search, Status, then Sync with Meta and New form. */
export const FormsFilterBarSkeleton: React.FC = () => (
  <WorkspaceFilterBarSkeleton filters={1} actions={[136, 108]} />
);

/**
 * The page-level placeholder, used by the route guards and the `<Suspense>`
 * fallback. It carries the same chrome the loaded page does, so the workspace
 * does not paint twice — once bare, then again with tabs and a toolbar on top.
 */
export const FormsPageSkeleton: React.FC = () => (
  <div
    className="flex h-full min-h-full min-w-0 flex-col bg-white"
    aria-busy="true"
    aria-label="Loading forms"
    data-testid="forms-page-skeleton"
  >
    <FormsTabsSkeleton />
    <FormsFilterBarSkeleton />
    <div className="min-h-0 min-w-0 flex-1 overflow-hidden px-[15px] pb-4 pt-4">
      <FormsTableSkeleton />
    </div>
  </div>
);

export default FormsPageSkeleton;
